'use client';
// src/hooks/useClub.js
// Subscribes to clubs/{clubId} doc + clubs/{clubId}/members subcollection.
// Derives the current user's role and admin status.
//
// `isAdmin` is an OR of three things:
//   1. The viewer is the club's ownerId (top-level doc field).
//   2. The viewer is a platform founder/support agent (JWT custom claim).
//      This lets Scrimly staff see the same admin affordances when helping
//      a club owner debug things, without needing a membership row.
//   3. The viewer's members/{uid}.role normalizes to a canonical club role
//      (`director` or `club_admin`). Legacy spellings (`owner`, `admin`)
//      are folded onto canonical via normalizeClubRole so old member docs
//      keep working.

import { useState, useEffect } from 'react';
import { doc, collection, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import usePlatformClaims from '@/hooks/usePlatformClaims';
import { normalizeClubRole, CLUB_ROLES } from '@/constants/roles';

export default function useClub(clubId) {
  const { firebaseUser } = useAuth();
  const { isFounder, isSupport } = usePlatformClaims();
  const [club, setClub]       = useState(null);
  const [members, setMembers] = useState([]);
  const [myRole, setMyRole]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to the club document
  useEffect(() => {
    if (!clubId) { setClub(null); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'clubs', clubId),
      (snap) => {
        if (!snap.exists()) { setClub(null); setLoading(false); return; }
        setClub({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => { console.error('[useClub] club doc error', err); setLoading(false); }
    );
    return unsub;
  }, [clubId]);

  // Subscribe to members subcollection
  useEffect(() => {
    if (!clubId) { setMembers([]); setMyRole(null); return; }
    const unsub = onSnapshot(
      collection(db, 'clubs', clubId, 'members'),
      (snap) => {
        const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        setMembers(list);
        const mine = list.find(m => m.uid === firebaseUser?.uid);
        setMyRole(mine?.role ?? null);
      },
      (err) => console.error('[useClub] members error', err)
    );
    return unsub;
  }, [clubId, firebaseUser?.uid]);

  // Auto-seed owner membership if the club exists but has no member record for this user
  useEffect(() => {
    if (!club || !firebaseUser?.uid) return;
    if (club.ownerId !== firebaseUser.uid) return;
    // Will be a no-op if the doc already exists (merge: true)
    setDoc(
      doc(db, 'clubs', club.id, 'members', firebaseUser.uid),
      {
        uid:         firebaseUser.uid,
        email:       firebaseUser.email || '',
        displayName: firebaseUser.displayName || '',
        role:        'owner',
        addedAt:     serverTimestamp(),
      },
      { merge: true }
    ).catch(e => console.error('[useClub] seed owner member failed', e));
  }, [club, firebaseUser]);

  const isOwner = club?.ownerId === firebaseUser?.uid;
  const isPlatformOverride = isFounder || isSupport;
  const canonicalMyRole = normalizeClubRole(myRole);
  const hasClubAdminRole =
    canonicalMyRole === CLUB_ROLES.DIRECTOR ||
    canonicalMyRole === CLUB_ROLES.CLUB_ADMIN;
  const isAdmin = isOwner || isPlatformOverride || hasClubAdminRole;

  return { club, members, myRole, isOwner, isAdmin, loading };
}
